import React, { useState } from 'react';
import { makeStyles } from 'src/makeStyles';
import get from 'lodash/get';
import { PAGE_SUB_TYPE, PAGE_TYPE } from 'constants/footPrintTracking';
import { FootPrintTracking } from 'components/Globals/FootPrintTracking';
import ArtistCreateAccountLink from 'components/ArtistCreateAccountLink';
import { ProductionSlug } from 'components/ProductionAccordion';
import ArtistsSearchFilters from 'components/ArtistsSearchFilters';
import FeaturedArtists from 'components/Artist/Display/FeaturedArtists';
import FeaturedProductions from 'components/Productions/Display/FeaturedProductions';
import SearchBg from 'components/SearchBg';
import Link from 'components/uiLibrary/Link';
import usePageContext from 'utils/hooks/usePageContext';
import { DATE_FORMATS, PRODUCTION_VALIDITY_IDENTIFIERS } from 'constants/index';
import LinkSections from './components/LinkSections';
import { Router, useTranslation } from '../../src/i18n';
import Layout, { LayoutContent } from '../../components/Layout';
import { colors } from '../../constants/styleConstants';
import route from '../../constants/routes';
import { getFlatFormValues } from '../../utils/formUtils';
import { filterViewMode, InteractiveFilterPanel } from '../InteractiveFilterPanel';
import { createDate } from '../../utils/date';
import { conditionallyRemoveViewByFiltersForRoute, futureDateUpTo1Years, pastDateUpTo3Years } from '../../utils/common';
import { fetchAppConstants } from '../../utils/queries/productionsQuery';

const useStyles = makeStyles()(theme => ({
  sectionsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    padding: '36px 0',
    [theme.breakpoints.down(1280)]: {
      flexDirection: 'column',
      padding: '20px 16px',
    },
  },
  sectionTitle: {
    color: colors.title,
    fontSize: '2.33rem',
    lineHeight: 1.1,
    marginBottom: 15,
    [theme.breakpoints.down('md')]: {
      fontSize: '1.5rem',
    },
  },
  section: {
    width: '100%',
    marginTop: 30,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${colors.grey}`,
    marginBottom: 5,
  },
  viewAll: {
    fontWeight: 'bold',
  },
  sectionRow: {
    display: 'flex',
    width: '100%',
    [theme.breakpoints.down('lg')]: {
      flexWrap: 'wrap',
    },
  },
  // search
  performanceSection: {
    padding: '0 25px 0 0',
    borderRight: `1px solid ${colors.background.dark}`,
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 48%',
    maxWidth: '48%',
    [theme.breakpoints.down(1280)]: {
      flex: '1 0 100%',
      maxWidth: '100%',
      borderRight: 0,
      padding: 0,
    },
  },
  interactiveFilterPanel: {
    background: colors.white,
    boxShadow: 'none',
  },
  artistSection: {
    paddingLeft: '25px',
    flex: '1 0 52%',
    maxWidth: '52%',
    marginTop: '11px',
    [theme.breakpoints.down(1280)]: {
      marginTop: 30,
      paddingLeft: 0,
      flex: '1 0 100%',
      maxWidth: '100%',
    },
  },
  searchContainer: {
    marginTop: '27px',
  },
  gallery: {
    padding: '20px 30px',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 0',
    },
  },
  bannerSection: {
    marginTop: 20,
    [theme.breakpoints.down(1280)]: {
      marginTop: 0,
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: 0,
    },
  },
}));

export const homeSections = {
  performanceSearch: {
    id: 'performances',
    title: 'operabase.general.FN_PERFORMANCESEARCH',
  },
  artists: {
    id: 'artists',
    title: 'operabase.general.FN_ARTISTSEARCH',
  },
  highlights: {
    id: 'highlights',
    title: 'operabase.general.m_HIGHLIGHTS',
  },
  performances: {
    id: 'performances',
    title: 'operabase.general.FN_PERFORMANCESTODAY',
  },
};

export const Home = () => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { permissions } = usePageContext();
  const { isLoggedIn } = permissions;
  const [expandedIndexes, setExpandedIndexes] = useState([]);
  const onToggleProductionSlug = panel => (event, isExpanded) => {
    setExpandedIndexes(state => (isExpanded ? [...state, panel] : state.filter(p => p !== panel)));
  };

  const { classes } = useStyles();
  const [performanceDateRange, setPerformanceDateRange] = useState({
    performanceStartDate: pastDateUpTo3Years,
    performanceEndDate: futureDateUpTo1Years,
  });

  React.useEffect(() => {
    fetchAppConstants().then(response => {
      const constantsData = response?.data?.data[0];
      const { PERFORMANCE_START_DATE: performanceStartDate, PERFORMANCE_END_DATE: performanceEndDate } = constantsData;
      setPerformanceDateRange({
        ...(performanceStartDate && { performanceStartDate: createDate(performanceStartDate).toDate() }),
        ...(performanceEndDate && { performanceEndDate: createDate(performanceEndDate).toDate() }),
      });
    });
  }, []);

  const searchProfilesWithCriteria = values => {
    const query = getFlatFormValues(values);
    const queryString = get(query, 'query');
    Router.push({
      pathname: route.ARTISTS,
      query: queryString ? { query: queryString } : {},
    });
  };

  const today = createDate(new Date()).format(DATE_FORMATS.FULL_DATE);

  const trackingData = {
    pageType: PAGE_TYPE.LANDING_PAGE,
    pageSubType: PAGE_SUB_TYPE.HOME,
  };

  return (
    <FootPrintTracking data={trackingData}>
      <Layout
        title={t('operabase.general.m_HOME')}
        seo={{
          title: t('operabase.general.SEO_TITLE_HOMEPAGE'),
          description: t('operabase.general.SEO_DESC_HOMEPAGE'),
          ogTitle: t('operabase.general.SEO_OG_TITLE_HOMEPAGE'),
        }}
      >
        <div className={classes.bannerSection}>
          <SearchBg />
        </div>
        <LayoutContent className={classes.sectionsBox}>
          <div className={classes.sectionRow}>
            <section className={classes.performanceSection}>
              <InteractiveFilterPanel
                className={classes.interactiveFilterPanel}
                viewMode={filterViewMode.COMPACT}
                onSubmit={values => {
                  const validQuery = conditionallyRemoveViewByFiltersForRoute(values);
                  Router.push(
                    {
                      pathname: route.PRODUCTIONS,
                      query: validQuery,
                    },
                    undefined,
                    {
                      shallow: false,
                    },
                  );
                }}
                onClear={() => {}}
                selectedValues={{}}
                performanceDateRange={performanceDateRange}
              />
            </section>
            <section className={classes.artistSection}>
              <h2 className={classes.sectionTitle}>{t(get(homeSections, 'artists.title'))}</h2>
              <div className={classes.searchContainer}>
                <ArtistsSearchFilters
                  fullWidth
                  variant="search"
                  t={t}
                  submitFunc={v => searchProfilesWithCriteria(v)}
                  initialValues=""
                />
                <ArtistCreateAccountLink spacing="10px 5px 0" />
                <div className={classes.gallery}>
                  <FeaturedArtists limit={14} showMoreLink />
                </div>
              </div>
            </section>
          </div>
          <section className={classes.section}>
            <FeaturedProductions
              limit={3}
              filters={{
                date_from: today,
                date_to: today,
                is_producer_approved: true,
                approved_by: PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN_AND_ORG,
                sort: 'random',
              }}
              renderHeader={() => (
                <div className={classes.sectionHeader}>
                  <h2 className={classes.sectionTitle}>{t(get(homeSections, 'performances.title'))}</h2>
                  <Link
                    href={`${route.PRODUCTIONS}\\?date_from=${today}&date_to=${today}`}
                    as={`${route.PRODUCTIONS}\\?date_from=${today}&date_to=${today}`}
                    className={classes.viewAll}
                  >
                    {t('operabase.general.VIEW_ALL')}
                  </Link>
                </div>
              )}
              renderSlug={({ production }) => {
                const isExpanded = expandedIndexes.includes(production?.id);

                return (
                  <ProductionSlug
                    key={production?.id}
                    viewType="production"
                    production={production}
                    isLoggedIn={isLoggedIn}
                    isExpanded={isExpanded}
                    handleChange={onToggleProductionSlug}
                  />
                );
              }}
            />
          </section>
          <section className={classes.section}>
            <LinkSections />
          </section>
        </LayoutContent>
      </Layout>
    </FootPrintTracking>
  );
};

export default Home;
